'use client';

import {
  findIconDefinition,
  IconDefinition,
  IconName,
  IconPrefix,
} from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon, FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { forwardRef, useMemo } from 'react';

import './icon-library-storefront';

export type IconProps = Omit<FontAwesomeIconProps, 'icon'> & { icon?: IconDefinition | string };

const PREFIX_FALLBACK_ORDER: IconPrefix[] = ['fas', 'far', 'fab'];

/**
 * Normalizes a CMS-supplied string to FontAwesome's `iconName` form.
 * Accepts: "magnifying-glass", "magnifyingGlass", "MagnifyingGlass",
 * "faMagnifyingGlass", or already-normalized "magnifying-glass".
 */
function normalizeIconName(input: string): IconName | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const stripped =
    trimmed.startsWith('fa') && /^fa[A-Z]/.test(trimmed) ? trimmed.slice(2) : trimmed;
  return stripped
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/[\s_]+/g, '-')
    .toLowerCase() as IconName;
}

function resolveStringIcon(name: string): IconDefinition | null {
  const iconName = normalizeIconName(name);
  if (!iconName) return null;
  for (const prefix of PREFIX_FALLBACK_ORDER) {
    const def = findIconDefinition({ prefix, iconName });
    if (def) return def;
  }
  return null;
}

export const Icon = forwardRef<SVGSVGElement, IconProps>(({ icon, size = 'sm', ...rest }, ref) => {
  const resolved = useMemo<IconDefinition | null>(() => {
    if (!icon) return null;
    if (typeof icon === 'string') return resolveStringIcon(icon);
    return icon;
  }, [icon]);

  if (!resolved) return null;
  return <FontAwesomeIcon ref={ref} icon={resolved} size={size} {...rest} />;
});

Icon.displayName = 'Icon';
